import { Badge, type DataTableColumn, Toggle } from "@design";
import { Star } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { AiModelResponse, ModelTierDefaultResponse } from "@/api";
import { Text } from "@/components/Text";
import { formatDate, formatNumber } from "@/i18n/format";
import { hostingOf, localDate, protocolOf, usageCount } from "./modelOptions";
import { useDescribeUsage } from "./useDescribeUsage";

function NameCell({ model, models }: Readonly<{ model: AiModelResponse; models: AiModelResponse[] }>) {
  const { t } = useTranslation("adminModels");
  const describeUsage = useDescribeUsage();
  const replacement = models.find((m) => m.id === model.replacementModelId);
  const notes = [
    model.isDeprecated &&
      replacement?.isEnabled &&
      !replacement.isDeprecated &&
      t("list.useInstead", { name: replacement.name }),
    model.isDeprecated && model.sunsetDate && t("list.sunset", { date: formatDate(localDate(model.sunsetDate)) }),
    model.agentCount != null &&
      usageCount(model) > 0 &&
      t("list.usedBy", { usage: describeUsage(model.agentCount, model.chatCount ?? 0) }),
  ].filter(Boolean);
  return (
    <div className="flex min-w-0 flex-col text-left">
      <span className="flex flex-wrap items-center gap-2">
        <Text as="span" variant="body-sm" className="truncate">
          {model.name}
        </Text>
        {model.isDeprecated && (
          <Badge size="compact" variant="warning">
            {t("list.deprecated")}
          </Badge>
        )}
      </span>
      <Text as="span" variant="caption" tone="tertiary" className="break-words">
        {t("list.idAndEndpoint", {
          id: model.id,
          endpoint: t(`hostings.${hostingOf(model)}.label`),
          protocol: t(`protocols.${protocolOf(model)}.label`),
        })}
      </Text>
      {notes.length > 0 && (
        <Text as="span" variant="caption" tone="warning">
          {notes.join(" ")}
        </Text>
      )}
    </div>
  );
}

function EnabledCell({
  model,
  canEdit,
  onChange,
}: Readonly<{ model: AiModelResponse; canEdit: boolean; onChange: (enabled: boolean) => void }>) {
  const { t } = useTranslation("adminModels");
  if (!canEdit) {
    return (
      <Badge variant={model.isEnabled ? "success" : "neutral"}>
        {t(model.isEnabled ? "list.enabled" : "list.disabled")}
      </Badge>
    );
  }
  return (
    <Toggle checked={model.isEnabled} onChange={onChange} aria-label={t("list.enabledFor", { name: model.name })} />
  );
}

export function useModelColumns({
  models,
  defaults,
  canEdit,
  onToggle,
}: Readonly<{
  models: AiModelResponse[];
  defaults: ModelTierDefaultResponse[];
  canEdit: boolean;
  onToggle: (model: AiModelResponse, enabled: boolean) => void;
}>) {
  const { t } = useTranslation("adminModels");
  const columns: DataTableColumn<AiModelResponse>[] = [
    {
      key: "name",
      header: t("columns.name"),
      mobile: "primary",
      render: (model) => <NameCell model={model} models={models} />,
    },
    {
      key: "cost",
      header: t("columns.cost"),
      render: (model) => (
        <Text as="span" variant="body-sm" tone="secondary">
          {model.costTier || t("list.none")}
        </Text>
      ),
    },
    {
      key: "context",
      header: t("columns.context"),
      align: "right",
      render: (model) => (
        <Text as="span" variant="body-sm" tone="secondary" className="tabular-nums">
          {formatNumber(model.contextWindow)}
        </Text>
      ),
    },
    {
      key: "defaults",
      header: t("columns.defaultFor"),
      render: (model) => (
        <span className="flex flex-wrap gap-1">
          {defaults
            .filter((td) => td.modelId === model.id)
            .map((td) => (
              <Badge key={td.tier} size="compact" variant="neutral" icon={<Star size={12} />}>
                {td.label}
              </Badge>
            ))}
        </span>
      ),
    },
    {
      key: "enabled",
      header: t("columns.enabled"),
      interactive: true,
      render: (model) => (
        <EnabledCell model={model} canEdit={canEdit} onChange={(enabled) => onToggle(model, enabled)} />
      ),
    },
  ];
  return columns;
}
